import { useState } from 'react';
import { Box, CircularProgress, Popover, Tooltip, Typography } from '@mui/material';
import { CalendarMonthOutlined as AgendaIcon } from '@mui/icons-material';
import { authFetch } from '../../utils/authHelpers';
import { BACKEND_URL } from '../../utils/apiConfig';
import { leesJson } from '../../utils/apiFout';
import { computeDayLoad, pctOfDay, DEFAULT_CAPACITY_RULES, DAGSTAAT } from '../../utils/capacityCalc';
import { getCapacityCellStyle } from '../../utils/capacityColors';

const FONT = '"Inter", system-ui, sans-serif';
const GEDEMPT = '#6b7280';
const ACCENT = '#526075';

// 📅 Mini-agenda naast de uitvoerder: hoe vol staan de komende twee weken, en waar valt
// deze taak? Zelfde kleuren als de planner-capaciteitsstrook (Joris, 1 sep); verhuisd uit
// de oude TaskDetailDialog. Data: person-tasks-range (taken + kalenderblokken).
export default function UitvoerderAgenda({ personId, taakStart, taakEind }) {
  const [anchor, setAnchor] = useState(null);
  const [dagen, setDagen] = useState(null);
  const [fout, setFout] = useState(false);

  const openen = async (e) => {
    e.stopPropagation();
    setAnchor(e.currentTarget);
    if (dagen || !personId) return;
    try {
      const dayISOs = [];
      const d = new Date();
      while (dayISOs.length < 10) {
        const dow = d.getDay();
        if (dow !== 0 && dow !== 6) {
          dayISOs.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
        }
        d.setDate(d.getDate() + 1);
      }
      const params = `startDate=${dayISOs[0]}&endDate=${dayISOs[dayISOs.length - 1]}&personIds[]=${encodeURIComponent(personId)}`;
      const r = await authFetch(`${BACKEND_URL}/Api/person-tasks-range?${params}`);
      const data = await leesJson(r, 'Werkdruk laden');
      const { uren, staat } = computeDayLoad(data[personId] || [], dayISOs, {}, DEFAULT_CAPACITY_RULES);
      setDagen({ dayISOs, uren, staat });
    } catch {
      setFout(true);
    }
  };

  const inTaak = (iso) => taakStart && iso >= taakStart && iso <= (taakEind || taakStart);
  const DAGNAMEN = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];

  return (
    <>
      <Tooltip title="Agenda van de uitvoerder" arrow>
        <Box component="button" type="button" onClick={openen} aria-label="Agenda van de uitvoerder"
          sx={{ display: 'inline-flex', border: 0, bgcolor: 'transparent', p: 0, color: '#9ca3af', cursor: 'pointer', ml: 0.25,
                '&:hover': { color: ACCENT } }}>
          <AgendaIcon sx={{ fontSize: 16 }} />
        </Box>
      </Tooltip>
      <Popover open={Boolean(anchor)} anchorEl={anchor}
        onClose={(e) => { e?.stopPropagation?.(); setAnchor(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { onClick: (e) => e.stopPropagation(), sx: { p: 1.5, borderRadius: '0.75rem' } } }}>
        {fout ? (
          <Typography sx={{ fontFamily: FONT, fontSize: '0.8rem', color: GEDEMPT }}>Agenda laden mislukt</Typography>
        ) : !dagen ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5 }}>
            <CircularProgress size={14} />
            <Typography sx={{ fontFamily: FONT, fontSize: '0.8rem', color: GEDEMPT }}>Agenda laden…</Typography>
          </Box>
        ) : (
          <Box>
            <Typography sx={{ fontFamily: FONT, fontSize: '0.72rem', fontWeight: 700, color: GEDEMPT, mb: 0.75 }}>
              Komende 10 werkdagen · rand = deze taak
            </Typography>
            <Box sx={{ display: 'flex', gap: '3px' }}>
              {dagen.dayISOs.map((iso) => {
                const isDraaidag = dagen.staat[iso] === DAGSTAAT.DRAAIDAG;
                const vrij = dagen.staat[iso] === DAGSTAAT.VRIJ;
                const uren = dagen.uren[iso] || 0;
                const pct = pctOfDay(uren, DEFAULT_CAPACITY_RULES.hoursPerDay);
                const stijl = vrij
                  ? { bg: '#f1f5f9', fg: '#94a3b8' }
                  : isDraaidag
                    ? { bg: '#fde68a', fg: '#92400e' }
                    : getCapacityCellStyle(pct);
                const dt = new Date(`${iso}T12:00:00`);
                return (
                  <Box key={iso} sx={{
                    width: 44, borderRadius: '0.4rem', px: 0.25, py: 0.5, textAlign: 'center',
                    bgcolor: stijl.bg || '#f1f5f9',
                    border: inTaak(iso) ? `2px solid ${ACCENT}` : '2px solid transparent',
                  }}>
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.62rem', color: GEDEMPT }}>
                      {DAGNAMEN[dt.getDay()]} {dt.getDate()}/{dt.getMonth() + 1}
                    </Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.72rem', fontWeight: 700,
                                      color: stijl.fg || stijl.text || '#1f2937' }}>
                      {vrij ? 'vrij' : isDraaidag ? 'draai' : `${Math.round(uren * 10) / 10}u`}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Popover>
    </>
  );
}
